import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import { formatMoney, notSetUpMessage, payoutLabel, requestPayments, stripeHostedUrl } from './api.js';
import { SignInGate } from '../SignIn.jsx';

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Company payments and the Stripe onboarding return/refresh page. Arriving here never marks anything ready. */
export default function CompanyPaymentsPage() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const organizationId = params.get('organization') || '';
  if (loading) return <div className="content-wrap"><p>Loading account…</p></div>;
  if (!user || !supabase) return <SignInGate next={`/supplier/payments${organizationId ? `?organization=${organizationId}` : ''}`}/>;
  if (!uuidPattern.test(organizationId)) return <div className="content-wrap account-page"><h1>Payments</h1><p>Open Payments from your <Link to="/supplier/company">company page</Link>.</p></div>;
  return <PaymentsWorkspace key={`${user.id}:${organizationId}`} organizationId={organizationId} expired={params.get('refresh') === '1'} />;
}

function PaymentsWorkspace({ organizationId, expired }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState({ legalName: '', address: '', companyNumber: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    let current = true;
    requestPayments(supabase, 'supplier-payment-status', { organizationId }).then((value) => {
      if (!current) return;
      setStatus(value); setError('');
      setForm({ legalName: value.invoiceProfile?.legalName || '', address: value.invoiceProfile?.address || '', companyNumber: value.invoiceProfile?.companyNumber || '' });
    }, (cause) => { if (current) { setStatus(null); setError(cause.message); } })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [organizationId, version]);
  const reload = () => { setLoading(true); setVersion((value) => value + 1); };
  async function run(action) {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (cause) { if (alive.current) setError(cause.message); } finally { if (alive.current) setBusy(false); }
  }
  const save = (event) => { event.preventDefault(); void run(async () => {
    const { invoiceProfile } = await requestPayments(supabase, 'save-supplier-invoice-profile', { organizationId, ...form });
    if (alive.current) { setStatus((current) => current && { ...current, invoiceProfile }); setNotice('Invoice details saved.'); }
  }); };
  const onboard = () => run(async () => {
    const { url } = await requestPayments(supabase, 'create-supplier-onboarding', { organizationId });
    window.location.assign(stripeHostedUrl(url, ['connect.stripe.com']));
  });

  const heading = <header className="coord-page-heading"><div><span className="account-kicker">PAYMENTS</span><h1>Get paid for your work.</h1><p>Customers pay your company directly through Stripe. Resident Service 360 takes no commission on job payments.</p></div></header>;
  if (loading && !status) return <div className="content-wrap account-page">{heading}<p>Loading payments…</p></div>;
  if (!status) return <div className="content-wrap account-page">{heading}<div role="alert"><p>{error || 'Could not load payments.'}</p><button className="button button-secondary" onClick={reload}>Try again</button></div></div>;
  const ready = status.chargesEnabled && status.detailsSubmitted;
  const account = !status.accountLinked ? 'Not connected' : ready ? 'Ready to take payments' : 'Setup not finished';
  return <div className="content-wrap account-page">{heading}
    {!status.configured && <p className="cloud-notice">{notSetUpMessage} You can still save your invoice details.</p>}
    {status.configured && <section className="account-content-panel"><h2>Stripe account</h2>
      {expired && !ready && <p className="cloud-notice">Your Stripe setup link expired. Continue setup to get a new one.</p>}
      <p><strong>{account}</strong></p>
      {status.accountLinked && !ready && <p>{status.requirementsDue > 0 ? `Stripe needs ${status.requirementsDue} more ${status.requirementsDue === 1 ? 'detail' : 'details'} from you.` : 'Stripe is checking your details. This can take a little while.'}</p>}
      {ready && <p>{status.payoutsEnabled ? 'Stripe pays money out to your bank account.' : 'Stripe still needs details before it can pay out to your bank.'}</p>}
      <div className="coord-inline-actions">
        {!(ready && status.requirementsDue === 0) && <button className="button button-blue" disabled={busy} onClick={() => void onboard()}>{status.accountLinked ? 'Continue Stripe setup' : 'Set up payments with Stripe'}</button>}
        <button className="button button-secondary" disabled={busy || loading} onClick={reload}>Check status again</button>
      </div>
    </section>}
    <form className="coord-form account-content-panel" onSubmit={save}><h2>Invoice details</h2><p>Shown on every invoice you send. Use your registered legal name and address.</p>
      <label>Legal name<input required minLength={2} maxLength={180} value={form.legalName} disabled={busy} onChange={(event) => { const legalName = event.target.value; setForm((value) => ({ ...value, legalName })); }} /></label>
      <label>Registered address<textarea required minLength={10} maxLength={1000} value={form.address} disabled={busy} onChange={(event) => { const address = event.target.value; setForm((value) => ({ ...value, address })); }} /></label>
      <label>Company number (optional)<input maxLength={80} value={form.companyNumber} disabled={busy} onChange={(event) => { const companyNumber = event.target.value; setForm((value) => ({ ...value, companyNumber })); }} /></label>
      <button className="button button-blue" disabled={busy}>Save invoice details</button>
    </form>
    {status.configured && status.accountLinked && <section className="account-content-panel"><h2>Payouts</h2>
      {status.payouts.length ? <ul className="coord-owned-list">{status.payouts.map((payout) => <li key={payout.id}>{formatMoney(payout.amount, payout.currency)} · {payoutLabel(payout.status)}{payout.arrivalDate ? ` · arrives ${new Date(payout.arrivalDate).toLocaleDateString('en-GB')}` : ''}</li>)}</ul> : <p>No payouts yet.</p>}
    </section>}
    {notice && <p role="status">{notice}</p>}
    {error && <p role="alert">{error}</p>}
    <p><Link to="/supplier/company">Back to company</Link></p>
  </div>;
}
